import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    firestore,
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    doc,
    updateDoc,
    where,
} from "./firebase";
import { Row, Col, Card, Button, Form, Spinner, Pagination } from "react-bootstrap";
import { arrayUnion, arrayRemove, deleteDoc } from "firebase/firestore";

const IDEA_CHAR_LIMIT = 1000;
// Replace with your Cloud Function URL
const SLACK_FUNCTION_URL = "https://us-central1-team-ideas.cloudfunctions.net/sendSlackNotification";

async function sendSlackNotification(message) {
    try {
        const response = await fetch(SLACK_FUNCTION_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message }),
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error sending Slack notification via cloud function:", error);
    }
}

function IdeaList({ user }) {
    const [ideas, setIdeas] = useState([]);
    const [ideaText, setIdeaText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("recent"); // "recent", "popular", or "mine"
    const [voting, setVoting] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setLoading(true);
        let q;
        if (filter === "mine") {
            q = query(
                collection(firestore, "ideas"),
                where("uid", "==", user.uid),
                orderBy("createdAt", "desc")
            );
        } else if (filter === "popular") {
            q = query(collection(firestore, "ideas"), orderBy("votesCount", "desc"));
        } else {
            q = query(collection(firestore, "ideas"), orderBy("createdAt", "desc"));
        }
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const ideasData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setIdeas(ideasData);
            setLoading(false);
            setCurrentPage(1); // Reset to first page on filter change or new data
        });
        return () => unsubscribe();
    }, [filter, user.uid]);

    const handleSubmitIdea = async (e) => {
        e.preventDefault();
        if (!ideaText.trim() || isSubmitting) return;

        setIsSubmitting(true);

        try {
            const docRef = await addDoc(collection(firestore, "ideas"), {
                text: ideaText,
                uid: user.uid,
                userName: user.displayName,
                userAvatar: user.photoURL,
                createdAt: new Date(),
                votesCount: 0,
                voters: [],
                commentsCount: 0, // initialize comment count
            });
            setIdeaText("");

            // Build the idea link (adjust the domain if needed)
            const ideaLink = `${window.location.origin}/idea/${docRef.id}`;
            // Updated formal notification text
            const message = `🔔 New Idea Submission: ${user.displayName} has submitted a new idea. Please review the idea here: ${ideaLink} and consider supporting it by voting or providing feedback via comments.`;
            sendSlackNotification(message);
        } catch (error) {
            console.error("Error submitting idea:", error);
        } finally {
            setIsSubmitting(false);
        }
    };



    const handleVote = async (idea) => {
        if (user.uid === idea.uid)
            return alert("You cannot vote for your own idea!");
        if (voting[idea.id]) return;

        setVoting((prev) => ({ ...prev, [idea.id]: true }));
        // Check if the current user has already voted by looking for an object with matching uid.
        const alreadyVoted = idea.voters && idea.voters.some(v => v.uid === user.uid);
        const ideaRef = doc(firestore, "ideas", idea.id);

        try {
            if (alreadyVoted) {
                await updateDoc(ideaRef, {
                    votesCount: idea.votesCount - 1,
                    voters: arrayRemove({ uid: user.uid, userName: user.displayName, userAvatar: user.photoURL }),
                });
            } else {
                await updateDoc(ideaRef, {
                    votesCount: idea.votesCount + 1,
                    voters: arrayUnion({ uid: user.uid, userName: user.displayName, userAvatar: user.photoURL }),
                });
            }
        } catch (error) {
            console.error("Error voting:", error);
        } finally {
            setVoting((prev) => ({ ...prev, [idea.id]: false }));
        }
    };

    // Client-side pagination: 10 ideas per page
    const ideasPerPage = 10;
    const totalPages = Math.ceil(ideas.length / ideasPerPage);
    const indexOfLastIdea = currentPage * ideasPerPage;
    const indexOfFirstIdea = indexOfLastIdea - ideasPerPage;
    const currentIdeas = ideas.slice(indexOfFirstIdea, indexOfLastIdea);

    return (
        <div className="container my-4" style={{ maxWidth: "800px" }}>
            <Card className="mb-4 p-3">
                <Form onSubmit={handleSubmitIdea}>
                    <Form.Group controlId="ideaInput">
                        <Form.Label>💡 Post a New Idea</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            maxLength={IDEA_CHAR_LIMIT}
                            placeholder="Share your idea..."
                            value={ideaText}
                            onChange={(e) => setIdeaText(e.target.value)}
                            disabled={isSubmitting}
                        />
                        <div className="text-end text-muted mt-1">
                            {ideaText.length} / {IDEA_CHAR_LIMIT} characters
                        </div>
                    </Form.Group>
                    <Button type="submit" variant="primary" disabled={!ideaText.trim() || isSubmitting} className="mt-2">
                        {isSubmitting ? "Posting..." : "Submit Idea"}
                    </Button>
                </Form>
            </Card>

            {/* Filter Bar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>All Ideas</h3>
                <div>
                    <Button variant={filter === "recent" ? "primary" : "outline-primary"} onClick={() => setFilter("recent")}>
                        🕒 Recent
                    </Button>
                    <Button variant={filter === "popular" ? "primary" : "outline-primary"} onClick={() => setFilter("popular")} className="ms-2">
                        🔥 Popular
                    </Button>
                    <Button variant={filter === "mine" ? "primary" : "outline-primary"} onClick={() => setFilter("mine")} className="ms-2">
                        👤 My Ideas
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <>
                    <Row>
                        {currentIdeas.map((idea) => (
                            <Col md={12} key={idea.id} className="mb-3">
                                <Card>
                                    <Card.Body>
                                        <Card.Text className="fs-5">{idea.text}</Card.Text>
                                        <div className="mb-3 d-flex justify-content-between">
                                            <div className="d-flex align-items-center mt-2">
                                                <img
                                                    src={idea.userAvatar}
                                                    alt={idea.userName}
                                                    className="rounded-circle"
                                                    width="30"
                                                    height="30"
                                                />
                                                <small className="text-muted ms-2">
                                                    {idea.userName} &bull;{" "}
                                                    {idea.createdAt && idea.createdAt.toDate
                                                        ? idea.createdAt.toDate().toLocaleString()
                                                        : new Date(idea.createdAt).toLocaleString()}
                                                </small>
                                            </div>
                                            <small className="text-muted ms-3">Comments: {idea.commentsCount || 0}</small>
                                        </div>

                                        <div className="d-flex justify-content-start gap-2 mt-3">
                                            <Button
                                                variant={
                                                    idea.voters && idea.voters.some(v => v.uid === user.uid)
                                                        ? "outline-danger"
                                                        : "outline-primary"
                                                }
                                                size="sm"
                                                onClick={() => handleVote(idea)}
                                                disabled={user.uid === idea.uid || voting[idea.id]}
                                            >
                                                {idea.voters && idea.voters.some(v => v.uid === user.uid)
                                                    ? `Unvote (${idea.votesCount})`
                                                    : `Vote (${idea.votesCount})`}
                                            </Button>
                                            <Button as={Link} to={`/idea/${idea.id}`} variant="secondary" size="sm">
                                                View Details
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    {totalPages > 1 && (
                        <Pagination className="justify-content-center">
                            <Pagination.Prev
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            />
                            {[...Array(totalPages)].map((_, index) => (
                                <Pagination.Item
                                    key={index + 1}
                                    active={index + 1 === currentPage}
                                    onClick={() => setCurrentPage(index + 1)}
                                >
                                    {index + 1}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            />
                        </Pagination>
                    )}
                </>
            )}
        </div>
    );
}

export default IdeaList;
